import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTable, FaProjectDiagram, FaCircle, FaNetworkWired, FaEthernet,
  FaWifi, FaShieldAlt, FaMapMarkerAlt, FaSyncAlt
} from 'react-icons/fa';
import { NetworkTopologyView } from './NetworksView';
import TableView from './TableView';
import axiosInstance from '../utils/axiosConfig';
import './AssetsView.css';

// Config de tipos de dispositivo
const DEVICE_TYPES = {
  router: { label: 'Router', icon: FaNetworkWired, color: '#3b82f6' },
  switch: { label: 'Switch', icon: FaEthernet, color: '#06b6d4' },
  access_point: { label: 'Access Point', icon: FaWifi, color: '#22c55e' },
  firewall: { label: 'Firewall', icon: FaShieldAlt, color: '#ef4444' },
};

// Celda de estado
const StatusCell = ({ isOnline }) => (
  <div className="flex justify-center">
    <FaCircle size={8} style={{ color: isOnline ? '#10b981' : '#d1d5db' }} aria-hidden="true" title={isOnline ? 'Online' : 'Offline'} />
  </div>
);

// Celda de tipo con icono
const DeviceTypeCell = ({ tipo }) => {
  const config = DEVICE_TYPES[tipo] || DEVICE_TYPES.router;
  const Icon = config.icon;
  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: config.color + '18' }}>
        <Icon size={13} style={{ color: config.color }} aria-hidden="true" />
      </div>
      <span className="text-sm">{config.label}</span>
    </div>
  );
};

function NetworksWrapper() {
  const [viewMode, setViewMode] = useState('tabla');
  const [data, setData] = useState({ dispositivos: [], conexiones: [], vlans: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [pinging, setPinging] = useState(false);

  useEffect(() => {
    if (viewMode === 'tabla') fetchData();
  }, [viewMode]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/api/redes/topologia');
      setData(response.data);
    } catch (err) {
      setError('Error al cargar datos de red');
    } finally {
      setLoading(false);
    }
  };

  const handlePingAll = async () => {
    setPinging(true);
    try {
      const response = await axiosInstance.post('/api/redes/ping-all');
      const results = response.data;
      setData(prev => ({
        ...prev,
        dispositivos: prev.dispositivos.map(d => {
          const ping = results.find(r => r.id === d.id);
          return ping ? { ...d, is_online: ping.is_online, last_check: ping.last_check } : d;
        }),
      }));
    } catch (err) {
      console.error('Error pinging:', err);
    } finally {
      setPinging(false);
    }
  };

  // Enriquecer dispositivos con info de conexiones y VLANs
  const tableData = useMemo(() => {
    return data.dispositivos.map(d => {
      // Contar conexiones
      const conns = data.conexiones.filter(c => c.origen_id === d.id || c.destino_id === d.id);
      // VLANs asociadas (via conexiones)
      const vlanIds = [...new Set(conns.map(c => c.vlan_id).filter(Boolean))];
      const deviceVlans = vlanIds.map(id => data.vlans.find(v => v.id === id)).filter(Boolean);

      return {
        ...d,
        conexiones_count: conns.length,
        vlans_list: deviceVlans,
      };
    });
  }, [data]);

  const totalDevices = tableData.length;
  const onlineCount = tableData.filter(d => d.is_online).length;

  const [columnVisibility, setColumnVisibility] = useState({
    is_online: true,
    tipo: true,
    nombre: true,
    ip: true,
    mac: true,
    marca: true,
    conexiones_count: true,
    vlans_list: true,
  });

  const columns = useMemo(() => [
    {
      header: "",
      accessorKey: "is_online",
      id: "is_online",
      size: 36,
      maxSize: 36,
      enableSorting: true,
      cell: ({ getValue }) => <StatusCell isOnline={getValue()} />,
    },
    {
      header: "Tipo",
      accessorKey: "tipo",
      size: 130,
      cell: ({ getValue }) => <DeviceTypeCell tipo={getValue()} />,
    },
    {
      header: "Nombre",
      accessorKey: "nombre",
      size: 180,
      cell: ({ row, getValue }) => (
        <div className="flex flex-col">
          <span className="text-sm font-semibold">{getValue()}</span>
          {row.original.notas && (
            <span className="text-[10px] text-gray-400 truncate max-w-[170px]" title={row.original.notas}>{row.original.notas}</span>
          )}
        </div>
      ),
    },
    {
      header: "IP",
      accessorKey: "ip",
      size: 140,
      cell: ({ getValue }) => {
        const ip = getValue();
        if (!ip) return <span className="text-gray-300 text-sm">—</span>;
        return <span className="font-mono text-sm">{ip}</span>;
      },
    },
    {
      header: "MAC",
      accessorKey: "mac",
      size: 150,
      cell: ({ getValue }) => {
        const mac = getValue();
        if (!mac) return <span className="text-gray-300 text-sm">—</span>;
        return <span className="font-mono text-xs text-gray-500">{mac}</span>;
      },
    },
    {
      header: "Marca / Modelo",
      accessorKey: "marca",
      size: 160,
      cell: ({ row }) => {
        const text = [row.original.marca, row.original.modelo].filter(Boolean).join(' ');
        return <span className="text-sm text-gray-600">{text || '—'}</span>;
      },
    },
    {
      header: "Conexiones",
      accessorKey: "conexiones_count",
      size: 90,
      cell: ({ getValue }) => {
        const count = getValue();
        if (!count) return <span className="text-gray-300 text-sm">—</span>;
        return <span className="text-sm font-medium text-center block" style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>;
      },
    },
    {
      header: "VLANs",
      accessorKey: "vlans_list",
      size: 160,
      enableSorting: false,
      cell: ({ getValue }) => {
        const vlans = getValue();
        if (!vlans || vlans.length === 0) return <span className="text-gray-300 text-sm">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {vlans.map(v => (
              <span
                key={v.id}
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: v.color + '20', color: v.color, border: `1px solid ${v.color}40` }}
                title={v.descripcion || v.nombre}
              >
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: v.color }} />
                {v.numero}
              </span>
            ))}
          </div>
        );
      },
    },
  ], []);

  const availableFilters = useMemo(() => [
    { id: 'tipo', placeholder: 'Todos los tipos', options: Object.keys(DEVICE_TYPES) },
  ], []);

  return (
    <div className={viewMode === 'topologia' ? '' : 'assets-view'}>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4" style={viewMode === 'topologia' ? { padding: '16px 16px 0' } : {}}>
        <div className="flex items-center gap-4">
          <h2 className="page-title">Redes</h2>
          {!loading && viewMode === 'tabla' && (
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span>{totalDevices} dispositivos <span className="text-green-500">({onlineCount} online)</span></span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {viewMode === 'tabla' && (
            <button
              className="btn-secondary"
              onClick={handlePingAll}
              disabled={pinging}
            >
              <FaSyncAlt className={pinging ? 'animate-spin' : ''} aria-hidden="true" />
              {pinging ? 'Verificando…' : 'Ping'}
            </button>
          )}

          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('tabla')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'tabla' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Vista tabla"
            >
              <FaTable size={12} aria-hidden="true" />
              Tabla
            </button>
            <button
              onClick={() => setViewMode('topologia')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'topologia' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Vista topologia"
            >
              <FaProjectDiagram size={12} aria-hidden="true" />
              Topologia
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {viewMode === 'tabla' ? (
          <motion.div
            key="tabla"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <TableView
              data={tableData}
              columns={columns}
              loading={loading}
              error={error}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              defaultPageSize={30}
              defaultSorting={[{ id: 'tipo', desc: false }]}
              activeActionMenu={activeActionMenu}
              setActiveActionMenu={setActiveActionMenu}
              availableFilters={availableFilters}
            />
          </motion.div>
        ) : (
          <motion.div
            key="topologia"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            style={{ height: 'calc(100vh - 120px)' }}
          >
            <NetworkTopologyView />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default NetworksWrapper;
